import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  FileText, Building2, User, MapPin, Phone, Calendar,
  Shield, CheckCircle, AlertCircle, Lock
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const US_STATES = [
  { code: "AL", name: "Alabama" }, { code: "AK", name: "Alaska" }, { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" }, { code: "CA", name: "California" }, { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" }, { code: "DE", name: "Delaware" }, { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" }, { code: "HI", name: "Hawaii" }, { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" }, { code: "IN", name: "Indiana" }, { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" }, { code: "KY", name: "Kentucky" }, { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" }, { code: "MD", name: "Maryland" }, { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" }, { code: "MN", name: "Minnesota" }, { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" }, { code: "MT", name: "Montana" }, { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" }, { code: "NH", name: "New Hampshire" }, { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" }, { code: "NY", name: "New York" }, { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" }, { code: "OH", name: "Ohio" }, { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" }, { code: "PA", name: "Pennsylvania" }, { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" }, { code: "SD", name: "South Dakota" }, { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" }, { code: "UT", name: "Utah" }, { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" }, { code: "WA", name: "Washington" }, { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" }, { code: "WY", name: "Wyoming" }, { code: "DC", name: "Washington DC" }
];

export const SellerTaxInfoForm = ({ onComplete, existingInfo = null }) => {
  const [formData, setFormData] = useState({
    legal_name: existingInfo?.legal_name || '',
    tax_id_type: existingInfo?.tax_id_type || 'ssn',
    tax_id: '',
    business_type: existingInfo?.business_type || 'individual',
    address_line1: existingInfo?.address_line1 || '',
    address_line2: existingInfo?.address_line2 || '',
    city: existingInfo?.city || '',
    state: existingInfo?.state || '',
    zip_code: existingInfo?.zip_code || '',
    phone: existingInfo?.phone || '',
    date_of_birth: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const formatTaxId = (value, type) => {
    const digits = value.replace(/\D/g, '');
    if (type === 'ssn') {
      if (digits.length <= 3) return digits;
      if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
      return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5, 9)}`;
    } else {
      if (digits.length <= 2) return digits;
      return `${digits.slice(0, 2)}-${digits.slice(2, 9)}`;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.legal_name || !formData.tax_id || !formData.address_line1 || 
        !formData.city || !formData.state || !formData.zip_code) {
      toast.error('Please fill in all required fields');
      return;
    }

    const taxIdDigits = formData.tax_id.replace(/\D/g, '');
    if (taxIdDigits.length !== 9) {
      toast.error(`Please enter a valid ${formData.tax_id_type === 'ssn' ? 'SSN' : 'EIN'} (9 digits)`);
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/seller/tax-info`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          tax_id: taxIdDigits
        })
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to save tax information');
      }

      toast.success('Tax information saved successfully!');
      onComplete?.();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Business Type Selection */}
      <div className="space-y-4">
        <Label className="text-base font-semibold">Business Type *</Label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { value: 'individual', label: 'Individual', icon: User },
            { value: 'sole_proprietor', label: 'Sole Proprietor', icon: User },
            { value: 'llc', label: 'LLC', icon: Building2 },
            { value: 'corporation', label: 'Corporation', icon: Building2 }
          ].map(({ value, label, icon: Icon }) => (
            <button
              key={value}
              type="button"
              onClick={() => setFormData({ ...formData, business_type: value })}
              className={`p-4 rounded-lg border-2 text-center transition ${
                formData.business_type === value 
                  ? 'border-emerald-500 bg-emerald-50' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <Icon className={`h-6 w-6 mx-auto mb-2 ${formData.business_type === value ? 'text-emerald-600' : 'text-gray-400'}`} />
              <span className="text-sm font-medium">{label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Legal Name */}
      <div>
        <Label className="mb-2 block">
          {formData.business_type === 'individual' ? 'Full Legal Name *' : 'Business Legal Name *'}
        </Label>
        <Input
          placeholder={formData.business_type === 'individual' ? 'John Michael Smith' : 'ABC Company LLC'}
          value={formData.legal_name}
          onChange={(e) => setFormData({ ...formData, legal_name: e.target.value })}
          required
        />
        <p className="text-xs text-muted-foreground mt-1">
          Must match exactly as shown on your tax documents
        </p>
      </div>

      {/* Tax ID */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="mb-2 block">Tax ID Type *</Label>
          <Select
            value={formData.tax_id_type}
            onValueChange={(value) => setFormData({ ...formData, tax_id_type: value, tax_id: '' })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ssn">SSN (Social Security Number)</SelectItem>
              <SelectItem value="ein">EIN (Employer ID Number)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="mb-2 block">
            {formData.tax_id_type === 'ssn' ? 'Social Security Number *' : 'Employer ID Number *'}
          </Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder={formData.tax_id_type === 'ssn' ? '___-__-____' : '__-_______'}
              className="pl-10"
              value={formData.tax_id}
              onChange={(e) => setFormData({ 
                ...formData, 
                tax_id: formatTaxId(e.target.value, formData.tax_id_type) 
              })}
              maxLength={formData.tax_id_type === 'ssn' ? 11 : 10}
              required
            />
          </div>
          <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
            <Shield className="h-3 w-3" /> Encrypted and secure. Required for 1099 tax reporting.
          </p>
        </div>
      </div>

      {/* Date of Birth (for individuals) */}
      {(formData.business_type === 'individual' || formData.tax_id_type === 'ssn') && (
        <div>
          <Label className="mb-2 block">Date of Birth *</Label>
          <Input
            type="date"
            value={formData.date_of_birth}
            onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
            required
          />
        </div>
      )}

      {/* Address */}
      <div className="space-y-4">
        <Label className="text-base font-semibold flex items-center gap-2">
          <MapPin className="h-4 w-4" /> Legal Address *
        </Label>
        
        <Input
          placeholder="Street Address"
          value={formData.address_line1}
          onChange={(e) => setFormData({ ...formData, address_line1: e.target.value })}
          required
        />
        
        <Input
          placeholder="Apt, Suite, Unit (optional)"
          value={formData.address_line2}
          onChange={(e) => setFormData({ ...formData, address_line2: e.target.value })}
        />
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="col-span-2 md:col-span-1">
            <Input
              placeholder="City"
              value={formData.city}
              onChange={(e) => setFormData({ ...formData, city: e.target.value })}
              required
            />
          </div>
          <div>
            <Select
              value={formData.state}
              onValueChange={(value) => setFormData({ ...formData, state: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="State" />
              </SelectTrigger>
              <SelectContent>
                {US_STATES.map((state) => (
                  <SelectItem key={state.code} value={state.code}>{state.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Input
              placeholder="ZIP Code"
              value={formData.zip_code}
              onChange={(e) => setFormData({ ...formData, zip_code: e.target.value.replace(/\D/g, '').slice(0, 5) })}
              maxLength={5}
              required
            />
          </div>
        </div>
      </div>

      {/* Phone */}
      <div>
        <Label className="mb-2 block flex items-center gap-2">
          <Phone className="h-4 w-4" /> Phone Number
        </Label>
        <Input
          type="tel"
          placeholder="(555) 555-5555"
          value={formData.phone}
          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
        />
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
          <FileText className="h-4 w-4" /> Why do we need this?
        </h4>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• Required by IRS for sellers earning $600+ per year</li>
          <li>• We'll send you a 1099-K form for tax filing</li>
          <li>• Your information is encrypted and secure</li>
          <li>• You can update this information anytime</li>
        </ul>
      </div>

      <Button 
        type="submit" 
        className="w-full bg-emerald-500 hover:bg-emerald-600"
        disabled={submitting}
      >
        {submitting ? 'Saving...' : 'Save Tax Information'}
      </Button>
    </form>
  );
};

export const SellerTaxInfoPage = ({ user }) => {
  const navigate = useNavigate();
  const [existingInfo, setExistingInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      toast.error('Please login to access seller settings');
      navigate('/');
      return;
    }

    const fetchTaxInfo = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API}/seller/tax-info`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        if (data.has_tax_info) {
          setExistingInfo(data);
        }
      } catch (error) {
        console.error('Error fetching tax info:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTaxInfo();
  }, [user, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen pt-52 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-52 pb-16 bg-slate-50" data-testid="seller-tax-page">
      <div className="container mx-auto px-4 md:px-6 max-w-2xl">
        <div className="text-center mb-8">
          <h1 className="font-outfit text-3xl font-bold mb-2">Seller Tax Information</h1>
          <p className="text-muted-foreground">
            Complete your tax information to start selling on BidZuu
          </p>
        </div>

        {existingInfo && (
          <Card className="mb-6 border-emerald-200 bg-emerald-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                {existingInfo.verified ? (
                  <CheckCircle className="h-6 w-6 text-emerald-600" />
                ) : (
                  <AlertCircle className="h-6 w-6 text-amber-500" />
                )}
                <div>
                  <p className="font-semibold">
                    {existingInfo.verified ? 'Verified Seller' : 'Information Submitted'}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {existingInfo.legal_name} • {existingInfo.tax_id_type.toUpperCase()} ending in {existingInfo.tax_id_last4}
                  </p>
                </div>
                {existingInfo.verified && (
                  <Badge className="ml-auto bg-emerald-500">Verified</Badge>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-emerald-600" />
              {existingInfo ? 'Update Tax Information' : 'Tax Information Required'}
            </CardTitle>
            <CardDescription>
              This information is required for IRS 1099-K reporting. BidZuu will report your earnings if you exceed $600 in a calendar year.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <SellerTaxInfoForm 
              existingInfo={existingInfo}
              onComplete={() => {
                toast.success('Tax information saved! You can now create listings.');
                navigate('/sell');
              }}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SellerTaxInfoPage;
